import { CombinedState, PayloadAction } from "@reduxjs/toolkit";
import { useDispatch, useSelector } from "react-redux";
import { AppThunk, RootState } from "../../store/store";
import { apiResponse, BaseApi } from "../../store/Stores/Api";
import { Entity } from "../../store/Stores/ApiStore";
import { ReduxStore, State } from "../../store/Stores/ReduxStore";

export enum AssetManagementSidepanelOption {
  Cloudia360Data = "CLOUDIA360DATA",
  Issues = "ISSUES",
  Selection = "SELECTECTION",
  Forms = "FORMS",
  AdditionalWork = "ADDITIONALWORK",
  Assignments = "ASSIGNMENTS",
  Calculations = "CALCULATIONS",
  GebouwAssistent = "GebouwAssistent",
  ProjectPhase = "ProjectPhase",
}

export enum CalculatorOfferOption {
  Detailed = "Detailed",
  Concise = "Concise",
  Open = "Open",
}

export enum CalculatorTemplateOption {
  Template = 1,
}
export enum TimeRegistrationOption {
  IntermediateSave = "IntermediateSave",
}

export enum StorageOption {
  AllowRootAccess = "AllowRootAccess",
}

export enum SettingsCategory {
  Tenant = "Tenant",
  Calculator = "Calculator",
  AssetManagement = "AssetManagement",
  TimeRegistration = "TimeRegistration",
  Storage = "Storage",
}

export enum TenantSettings {
  Colors = "Colors",
}

export enum AssetManagementSettings {
  Sidepanel = "Sidepanel",
}

export enum CalculatorSettings {
  OfferSettings = "OfferSettings",
  TemplateSettings = "TemplateSettings",
}

export enum TimeRegistrationSettings {
  IntermediateSave = "IntermediateSave",
}

export declare interface TenantColorSetting {
  name: string;
  color: string;
}

export declare interface SettingsEntity<T> extends Entity {
  name?: string;
  category?: string;
  type?: string;
  value?: T;
}

class SettingsApi extends BaseApi<SettingsEntity<any>, SettingsEntity<any>> {
  public constructor() {
    super("/settings");
  }

  public fetchAssetManagementSettings = (): Promise<
    apiResponse<SettingsEntity<any>>
  > => {
    return this.fetch({ category: SettingsCategory.AssetManagement });
  };

  public fetchCalculatorOfferSettings = (): Promise<
    apiResponse<SettingsEntity<any>>
  > => {
    return this.fetch({ category: SettingsCategory.Calculator });
  };

  public fetchTimeRegistrationSettings = (): Promise<
    apiResponse<SettingsEntity<any>>
  > => {
    return this.fetch({ category: SettingsCategory.TimeRegistration });
  };

  public fetchTenantColorSettings = (): Promise<
    apiResponse<SettingsEntity<any>>
  > => {
    return this.fetch({
      category: SettingsCategory.Tenant,
      name: TenantSettings.Colors,
    });
  };

  public fetchStorageSettings = (): Promise<
    apiResponse<SettingsEntity<any>>
  > => {
    return this.fetch({
      category: SettingsCategory.Storage,
    });
  };
}

export const settingsApi = new SettingsApi();

interface SettingsState extends State {
  assetManagementSidepanel: SettingsEntity<any>;
  calculatorOfferSettings: SettingsEntity<any>;
  calculatorTemplateSettings: SettingsEntity<any>;
  timeRegistrationSettings: SettingsEntity<any>;
  tenantColorSettings: SettingsEntity<TenantColorSetting[]>;
  status: "pending" | "idle";
}

class SettingsStore extends ReduxStore<SettingsState> {
  public readonly assetManagementSidepanelSettings = (
    rootState: RootState
  ): SettingsEntity<any> | undefined =>
    this.selector(rootState, "assetManagementSidepanel");
  public readonly calculatorOfferSettings = (
    rootState: RootState
  ): SettingsEntity<any> | undefined =>
    this.selector(rootState, "calculatorOfferSettings");
  public readonly calculatorTemplateSettings = (
    rootState: RootState
  ): SettingsEntity<any> | undefined =>
    this.selector(rootState, "calculatorTemplateSettings");
  public readonly timeRegistrationSettings = (
    rootState: RootState
  ): SettingsEntity<any> | undefined =>
    this.selector(rootState, "timeRegistrationSettings");
  public readonly tenantColorSettings = (
    rootState: RootState
  ): SettingsEntity<any> | undefined =>
    this.selector(rootState, "tenantColorSettings");
  public readonly status = (rootState: RootState): "pending" | "idle" =>
    this.selector(rootState, "status");

  public constructor() {
    super(
      Object.assign({
        assetManagementSidepanelSettings: {
          category: SettingsCategory.AssetManagement,
          name: AssetManagementSettings.Sidepanel,
          type: "list",
        },
        status: "idle",
      } as unknown as SettingsState)
    );

    const storeReducers = {
      setAssetManagementSidepanelSettings(
        state: SettingsState,
        action: PayloadAction<SettingsState["assetManagementSidepanel"]>
      ) {
        let data = undefined;
        if (!!action.payload) {
          data = action.payload;
        } else {
          data = {
            category: SettingsCategory.AssetManagement,
            name: AssetManagementSettings.Sidepanel,
            value: [],
          } as SettingsState["assetManagementSidepanel"];
        }
        state.assetManagementSidepanel = data;
      },
      setCalculatorOfferSettings(
        state: SettingsState,
        action: PayloadAction<SettingsState["calculatorOfferSettings"]>
      ) {
        let data = undefined;
        if (!!action.payload) {
          data = action.payload;
        } else {
          data = {
            category: SettingsCategory.Calculator,
            name: CalculatorSettings.OfferSettings,
            value: [],
          } as SettingsState["calculatorOfferSettings"];
        }
        state.calculatorOfferSettings = data;
      },
      setCalculatorTemplateSettings(
        state: SettingsState,
        action: PayloadAction<SettingsState["calculatorTemplateSettings"]>
      ) {
        let data = undefined;
        if (!!action.payload) {
          data = action.payload;
        } else {
          data = {
            category: SettingsCategory.Calculator,
            name: CalculatorSettings.TemplateSettings,
            type: "boolean",
            value: { useTemplates: 0 },
          } as SettingsState["calculatorTemplateSettings"];
        }
        state.calculatorTemplateSettings = data;
      },
      setTimeRegistrationSettings(
        state: SettingsState,
        action: PayloadAction<SettingsState["timeRegistrationSettings"]>
      ) {
        let data = undefined;
        if (!!action.payload) {
          data = action.payload;
        } else {
          data = {
            category: SettingsCategory.TimeRegistration,
            name: TimeRegistrationSettings.IntermediateSave,
            value: [],
          } as SettingsState["timeRegistrationSettings"];
        }
        state.timeRegistrationSettings = data;
      },
      setTenantColorSettings(
        state: SettingsState,
        action: PayloadAction<SettingsState["tenantColorSettings"]>
      ) {
        if (!!action.payload) {
          state.tenantColorSettings = action.payload;
        } else {
          const data: SettingsEntity<any> = {
            category: SettingsCategory.Tenant,
            name: TenantSettings.Colors,
            type: "list",
            value: [],
          };
          data.value = [
            {
              name: "background",
              color: "#f6f6f6",
            },
            {
              name: "text",
              color: "#555",
            },
            {
              name: "primary",
              color: "#33006a",
            },
            {
              name: "primaryContrast",
              color: "#fff",
            },
            {
              name: "secondary",
              color: "#ff6600",
            },
            {
              name: "secondaryContrast",
              color: "#000",
            },
          ] as TenantColorSetting[];

          state.tenantColorSettings = data;
        }
      },
      setStatus(
        state: SettingsState,
        action: PayloadAction<SettingsState["status"]>
      ) {
        state.status = action.payload;
      },
    };
    this.addReducers(storeReducers);
  }

  public setPending = () => this.actions().setStatus("pending");
  public setIdle = () => this.actions().setStatus("idle");

  public setAssetManagementSidepanelSettings = (
    value?: SettingsState["assetManagementSidepanel"]
  ) => this.actions().setAssetManagementSidepanelSettings(value);

  public setCalculatorOfferSettings = (
    value?: SettingsState["calculatorOfferSettings"]
  ) => this.actions().setCalculatorOfferSettings(value);

  public setCalculatorTemplateSettings = (
    value?: SettingsState["calculatorTemplateSettings"]
  ) => this.actions().setCalculatorTemplateSettings(value);

  public setTimeRegistrationSettings = (
    value?: SettingsState["timeRegistrationSettings"]
  ) => this.actions().setTimeRegistrationSettings(value);

  public setTenantColorSettings = (
    value?: SettingsState["tenantColorSettings"]
  ) => this.actions().setTenantColorSettings(value);

  public saveTenantColorSettings =
    (): AppThunk => async (dispatch, getState) => {
      try {
        if (Object.keys(getState()).includes(this.name)) {
          // dispatch(this.actions().setLoading(true));
          const state: CombinedState<any> = getState();

          const colorSettings = state[this.name].tenantColorSettings;

          settingsApi.save(colorSettings).then((response) => {
            if (!!response) {
              dispatch(settingsStore.setTenantColorSettings(response));
            }
          });
        }
      } catch (e) {}
    };

  public loadTenantColorSettings =
    (): AppThunk => async (dispatch, getState) => {
      try {
        settingsApi.fetchTenantColorSettings().then((response) => {
          if (!!response) {
            const data = response["hydra:member"];
            const tenantSettings = data.find(
              (item) => item.name === TenantSettings.Colors
            );

            dispatch(settingsStore.setTenantColorSettings(tenantSettings));
          }
        });
      } catch (e) {}
    };
}

export const settingsStore = new SettingsStore();

export const useAssetManagementSidepanelSettings = () => {
  const settings = useSelector(settingsStore.assetManagementSidepanelSettings);
  const dispatch = useDispatch();

  if (settings === undefined) {
    settingsApi.fetchAssetManagementSettings().then((response) => {
      let data: any = [];
      if (response && response["hydra:member"]) {
        data = response["hydra:member"];
      }
      const sidePanelSettings = data.find(
        (item: any) => item.name === AssetManagementSettings.Sidepanel
      );

      dispatch(
        settingsStore.setAssetManagementSidepanelSettings(sidePanelSettings)
      );
    });
  }

  return settings;
};

export const useCalculatorOfferSettings = () => {
  const settings = useSelector(settingsStore.calculatorOfferSettings);
  const dispatch = useDispatch();

  if (settings === undefined) {
    settingsApi.fetchCalculatorOfferSettings().then((response) => {
      let data: any = [];
      if (response && response["hydra:member"]) {
        data = response["hydra:member"];
      }

      const calculationOfferSettings = data.find(
        (item: any) => item.name === CalculatorSettings.OfferSettings
      );

      dispatch(
        settingsStore.setCalculatorOfferSettings(calculationOfferSettings)
      );
    });
  }

  return settings;
};

export const useCalculatorTemplateSettings = () => {
  const settings = useSelector(settingsStore.calculatorTemplateSettings);
  const status = useSelector(settingsStore.status);
  const dispatch = useDispatch();

  if (settings === undefined && status === "idle") {
    dispatch(settingsStore.setPending());
    settingsApi
      .fetchCalculatorOfferSettings()
      .then((response) => {
        let data: any = [];
        if (response && response["hydra:member"]) {
          data = response["hydra:member"];
        }

        const calculationTemplateSettings = data.find(
          (item: any) => item.name === CalculatorSettings.TemplateSettings
        );

        dispatch(
          settingsStore.setCalculatorTemplateSettings(
            calculationTemplateSettings
          )
        );
      })
      .finally(() => {
        dispatch(settingsStore.setIdle());
      });
  }

  return settings;
};

export const useCalculatorTemplates = (): boolean => {
  const settings = useCalculatorTemplateSettings();
  return !!settings?.value?.useTemplates;
};

export const useTimeRegistrationSettings = () => {
  const settings = useSelector(settingsStore.timeRegistrationSettings);
  const dispatch = useDispatch();

  if (settings === undefined) {
    settingsApi.fetchTimeRegistrationSettings().then((response) => {
      const data = response["hydra:member"];

      const timeRegistrationSettings = data.find(
        (item) => item.name === TimeRegistrationSettings.IntermediateSave
      );

      dispatch(
        settingsStore.setTimeRegistrationSettings(timeRegistrationSettings)
      );
    });
  }

  return settings;
};

export const useTenantColorSettings = ():
  | SettingsEntity<TenantColorSetting[]>
  | undefined => {
  const settings = useSelector(settingsStore.tenantColorSettings);

  if (!!settings) {
    return settings;
  }

  return undefined;
};

export const useTenantColorSettingValues = ():
  | TenantColorSetting[]
  | undefined => {
  const settings = useTenantColorSettings();
  if (!!settings) {
    return settings?.value;
  }

  return [];
};

export const useTenantColorSetting = (
  identifier: "background" | "text" | "primary" | "secondary"
): string | undefined => {
  const settings = useTenantColorSettingValues();
  if (!!settings) {
    return settings?.find((s: TenantColorSetting) => s.name === identifier)
      ?.color;
  }

  return undefined;
};
